import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Save, Package, Link, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyBoxSize = {
  size: '',
  price: '',
  name: '',
  shopify_link: '',
};

export const BoxSizeManagement = () => {
  const [boxSizes, setBoxSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [formData, setFormData] = useState(emptyBoxSize);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  useEffect(() => {
    fetchBoxSizes();
  }, []);

  const fetchBoxSizes = async () => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const response = await axios.get(`${API}/admin/box-sizes`, { headers });
      setBoxSizes(response.data);
    } catch (error) {
      toast.error('Failed to load box sizes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      size: parseInt(formData.size),
      price: parseFloat(formData.price),
      name: formData.name,
      shopify_link: formData.shopify_link,
    };

    try {
      const headers = getAuthHeader();
      if (!headers) return;

      if (editingSize !== null) {
        await axios.put(`${API}/admin/box-sizes/${editingSize}`, payload, { headers });
        toast.success('Box size updated successfully');
      } else {
        await axios.post(`${API}/admin/box-sizes`, payload, { headers });
        toast.success('Box size created successfully');
      }

      setShowForm(false);
      setEditingSize(null);
      setFormData(emptyBoxSize);
      fetchBoxSizes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save box size');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (box) => {
    setEditingSize(box.size);
    setFormData({
      size: box.size.toString(),
      price: box.price.toString(),
      name: box.name,
      shopify_link: box.shopify_link || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (size) => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;

      await axios.delete(`${API}/admin/box-sizes/${size}`, { headers });
      toast.success('Box size deleted');
      setDeleteConfirm(null);
      fetchBoxSizes();
    } catch (error) {
      toast.error('Failed to delete box size');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSize(null);
    setFormData(emptyBoxSize);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FA49C]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-[#222]">Box Size Management</h2>
          <p className="text-sm text-[#666]">{boxSizes.length} box sizes configured</p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white flex items-center gap-2"
          data-testid="add-boxsize-btn"
        >
          <Plus className="w-4 h-4" />
          Add Box Size
        </Button>
      </div>

      {/* Box Size Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={handleCancel}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-lg font-bold text-[#222]">
                  {editingSize !== null ? 'Edit Box Size' : 'Add New Box Size'}
                </h3>
                <button onClick={handleCancel} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Size & Price */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#666] mb-1">Number of Samples *</label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.size}
                      onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                      placeholder="e.g. 3"
                      required
                      disabled={editingSize !== null}
                      data-testid="boxsize-size-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#666] mb-1">Price (£) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="e.g. 2.99"
                      required
                      data-testid="boxsize-price-input"
                    />
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-[#666] mb-1">Display Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Starter Box"
                    required
                    data-testid="boxsize-name-input"
                  />
                </div>

                {/* Shopify Link */}
                <div>
                  <label className="block text-sm font-medium text-[#666] mb-1">
                    Shopify Cart Link *
                  </label>
                  <Input
                    value={formData.shopify_link}
                    onChange={(e) => setFormData({ ...formData, shopify_link: e.target.value })}
                    placeholder="https://ultimatepetfoods.co.uk/cart/..."
                    required
                    data-testid="boxsize-link-input"
                  />
                  <p className="text-xs text-[#999] mt-1">
                    The Shopify cart URL customers are redirected to after checkout
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-[#0FA49C] hover:bg-[#0d8f88] text-white flex items-center justify-center gap-2"
                    data-testid="save-boxsize-btn"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        {editingSize !== null ? 'Update' : 'Create'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#222]">Delete Box Size?</h3>
                  <p className="text-sm text-[#666]">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-sm text-[#666] mb-6">
                Are you sure you want to delete the <strong>{deleteConfirm.name}</strong> ({deleteConfirm.size} samples)?
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDelete(deleteConfirm.size)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-boxsize-btn"
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Box Sizes Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {boxSizes.map((box) => (
          <div
            key={box.size}
            className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#0FA49C]/10 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-[#0FA49C]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#222]">{box.name}</h3>
                  <p className="text-sm text-[#666]">{box.size} samples</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-[#0FA49C]">£{box.price.toFixed(2)}</span>
            </div>
            
            {box.shopify_link && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-xs text-[#666] mb-1">
                  <Link className="w-3 h-3" />
                  Shopify Link
                </div>
                <p className="text-xs text-[#999] truncate" title={box.shopify_link}>
                  {box.shopify_link}
                </p>
              </div>
            )}

            {!box.shopify_link && (
              <div className="mb-4 p-3 bg-orange-50 rounded-lg flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-500" />
                <span className="text-xs text-orange-600">No Shopify link configured</span>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handleEdit(box)}
                className="flex-1 flex items-center justify-center gap-2"
                data-testid={`edit-boxsize-${box.size}`}
              >
                <Pencil className="w-4 h-4" />
                Edit
              </Button>
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(box)}
                className="flex items-center justify-center gap-2 text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                data-testid={`delete-boxsize-${box.size}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}

        {boxSizes.length === 0 && (
          <div className="col-span-full text-center py-12 text-[#666]">
            <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No box sizes configured yet</p>
            <p className="text-sm">Click &quot;Add Box Size&quot; to get started</p>
          </div>
        )}
      </div>
    </div>
  );
};
